import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FIT } from "@/constants/testIds";

export default function AIWorkoutGenerator() {
  const [form, setForm] = useState({
    goal: "Build lean muscle and improve conditioning",
    duration_min: 45,
    difficulty: "Intermediate",
    equipment: "Barbell, kettlebells, bodyweight",
    focus_area: "full body",
    client_name: "",
  });
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setWorkout(null);
    try {
      const { data } = await api.post("/fitness/ai/workout", {
        ...form,
        duration_min: Number(form.duration_min) || 45,
      });
      setWorkout(data);
    } catch (e) {
      toast.error("AI could not generate a workout right now. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!workout) return;
    try {
      await api.post("/fitness/workouts", workout);
      toast.success("Workout saved to your library.");
    } catch {
      toast.error("Could not save workout.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Fitness · AI</div>
        <h1 className="font-display text-4xl">AI workout generator</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-xl">
          Claude Sonnet 4.5 designs a personalised programme in seconds — grounded in your equipment
          and your client's goals.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-6 space-y-4 lg:col-span-1">
          <div>
            <Label>Goal</Label>
            <Input data-testid={FIT.aiGoal} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Duration (min)</Label>
            <Input data-testid={FIT.aiDuration} type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Difficulty</Label>
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Beginner">Beginner</SelectItem>
                <SelectItem value="Intermediate">Intermediate</SelectItem>
                <SelectItem value="Advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Equipment available</Label>
            <Input value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Focus area</Label>
            <Input value={form.focus_area} onChange={(e) => setForm({ ...form, focus_area: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Client name (optional)</Label>
            <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="mt-1.5" />
          </div>
          <Button
            data-testid={FIT.aiGenerate}
            onClick={generate}
            disabled={loading}
            className="w-full rounded-full gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Generating…" : "Generate workout"}
          </Button>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2 min-h-[500px]">
          {!workout && !loading && (
            <div className="h-full grid place-items-center text-center text-muted-foreground p-8">
              <div>
                <Sparkles className="h-8 w-8 mx-auto text-primary mb-3" />
                <div className="font-display text-2xl">Your workout will appear here.</div>
                <div className="text-sm mt-1">Adjust inputs on the left, then generate.</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="h-full grid place-items-center text-muted-foreground">
              <div className="text-center">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
                <div className="mt-3 text-sm">Designing your workout…</div>
              </div>
            </div>
          )}
          {workout && (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="font-display text-3xl leading-tight">{workout.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">{workout.description}</div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-primary/10 text-primary px-2.5 py-1">{workout.difficulty}</span>
                    <span className="rounded-full border border-border px-2.5 py-1">{workout.duration_min} min</span>
                    {workout.focus && <span className="rounded-full border border-border px-2.5 py-1">{workout.focus}</span>}
                  </div>
                </div>
                <Button onClick={save} className="rounded-full gap-2">
                  <Save className="h-4 w-4" /> Save
                </Button>
              </div>
              <ol className="space-y-2 mt-6">
                {(workout.blocks || []).map((b, i) => (
                  <li key={i} className="rounded-md border border-border p-4 flex items-baseline gap-4">
                    <span className="font-display text-xl text-muted-foreground w-8">{i + 1}</span>
                    <div className="flex-1">
                      <div className="font-medium">{b.exercise_name}</div>
                      {b.notes && <div className="text-xs text-muted-foreground mt-0.5">{b.notes}</div>}
                    </div>
                    <div className="text-sm text-muted-foreground text-right">
                      <div>{b.sets ?? "—"} × {b.reps ?? "—"}</div>
                      {b.rest_seconds ? <div className="text-xs">rest {b.rest_seconds}s</div> : null}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
